import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import Feature from "../../Feature";
import errorToast from "../../../components/toast/ErrorToast";

export default class LeadPipelineFeature extends Feature {
    constructor(application) {
        super(application);
        this._columns = [];
        this._filterOptions = {
            commercialChannels: [],
            spansSeveralCommercialChannels: false,
            showsResponsibleFilter: false,
            responsibles: [],
            howTheyKnewUsNames: [],
        };
        this._filters = {
            fantasyName: '',
            responsibleEmail: '',
            howTheyKnewUs: '',
            commercialChannel: '',
            pipelineAge: '',
        };
        this._stageBeingDraggedFrom = null;
        this._lastPipelineRequestNumber = 0;

        makeObservable(this, {
            _columns: observable,
            _filterOptions: observable,
            _filters: observable,
            _stageBeingDraggedFrom: observable,
            columns: computed,
            filters: computed,
            commercialChannelsToChooseFrom: computed,
            showsCommercialChannelFilter: computed,
            showsResponsibleFilter: computed,
            responsiblesToChooseFrom: computed,
            howTheyKnewUsNames: computed,
            startDraggingFromStage: action,
            stopDragging: action,
            _storePipeline: action,
            _storeFilterOptions: action,
            _storeFilter: action,
        });
    }

    async load() {
        await this._loadFilterOptions();
        await this._loadPipeline();
        this.finishLoading();
    }

    get columns() {
        return this._columns;
    }

    get filters() {
        return this._filters;
    }

    get commercialChannelsToChooseFrom() {
        return this._filterOptions.commercialChannels;
    }

    get showsCommercialChannelFilter() {
        return this._filterOptions.spansSeveralCommercialChannels;
    }

    get showsResponsibleFilter() {
        return this._filterOptions.showsResponsibleFilter;
    }

    get responsiblesToChooseFrom() {
        return this._filterOptions.responsibles;
    }

    get howTheyKnewUsNames() {
        return this._filterOptions.howTheyKnewUsNames;
    }

    isLeadFromStageDraggable(stage) {
        return this._manuallyReachableStagesFrom(stage).length > 0;
    }

    acceptsDropOfLeadDraggedOverStage(stage) {
        return this._stageBeingDraggedFrom !== null
            && this._manuallyReachableStagesFrom(this._stageBeingDraggedFrom).includes(stage);
    }

    startDraggingFromStage(stage) {
        this._stageBeingDraggedFrom = stage;
    }

    stopDragging() {
        this._stageBeingDraggedFrom = null;
    }

    async moveLeadTo(leadId, sourceStage, targetStage) {
        if (this._manuallyReachableStagesFrom(sourceStage).includes(targetStage)) {
            const responseHandler = new ApiResponseHandler({
                handlesSuccess: () => this._loadPipeline(),
                handlesError: (response) => response.errors().forEach((error) => errorToast(error.text)),
            });
            return this._application.apiClient().editLeadStage({leadId, stage: targetStage}, responseHandler);
        }
    }

    async editFantasyNameFilter(fantasyName) {
        this._storeFilter('fantasyName', fantasyName);
        return this._loadPipeline();
    }

    async editResponsibleEmailFilter(responsibleEmail) {
        this._storeFilter('responsibleEmail', responsibleEmail);
        return this._loadPipeline();
    }

    async editHowTheyKnewUsFilter(howTheyKnewUs) {
        this._storeFilter('howTheyKnewUs', howTheyKnewUs);
        return this._loadPipeline();
    }

    async editCommercialChannelFilter(commercialChannel) {
        this._storeFilter('commercialChannel', commercialChannel);
        return this._loadPipeline();
    }

    async editPipelineAgeFilter(pipelineAge) {
        this._storeFilter('pipelineAge', pipelineAge);
        return this._loadPipeline();
    }

    _manuallyReachableStagesFrom(stage) {
        const column = this._columns.find((pipelineColumn) => pipelineColumn.stage === stage);
        return column ? column.manuallyReachableStages : [];
    }

    async _loadFilterOptions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => this._storeFilterOptions(response),
            handlesError: () => errorToast('No se pudieron obtener los filtros del pipeline.'),
        });
        return this._application.apiClient().leadPipelineFilterOptions(responseHandler);
    }

    async _loadPipeline() {
        this._lastPipelineRequestNumber += 1;
        const pipelineRequestNumber = this._lastPipelineRequestNumber;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => this._storePipelineIfItIsTheLastRequested(pipelineRequestNumber, response),
            handlesError: () => errorToast('No se pudo obtener el pipeline de leads.'),
        });
        return this._application.apiClient().leadPipeline(this._filters, responseHandler);
    }

    _storePipelineIfItIsTheLastRequested(pipelineRequestNumber, response) {
        if (pipelineRequestNumber === this._lastPipelineRequestNumber) {
            this._storePipeline(response);
        }
    }

    _storePipeline(response) {
        this._columns = response.columns();
    }

    _storeFilterOptions(response) {
        this._filterOptions = {
            commercialChannels: response.commercialChannels(),
            spansSeveralCommercialChannels: response.spansSeveralCommercialChannels(),
            showsResponsibleFilter: response.showsResponsibleFilter(),
            responsibles: response.responsibles(),
            howTheyKnewUsNames: response.howTheyKnewUsNames(),
        };
    }

    _storeFilter(filterName, value) {
        this._filters[filterName] = value;
    }
}
